import React, { useRef } from 'react';
import { useSpring, useTransform, useMotionValue, useReducedMotion } from 'framer-motion';
import { motion } from '../lib/motionShim';
import PhoneFrame from './PhoneFrame';
import { useViewportProgress } from '../lib/useViewportProgress';

/**
 * A PhoneFrame that lives in 3D:
 *  — scroll-driven: enters rotated and low, straightens to its resting tilt
 *    as it crosses the viewport center, leans away on exit
 *  — hover-driven: tilts toward the cursor on top of the scroll pose
 * `dir` flips the resting side (+1 tilts right-facing, -1 left-facing).
 * Respects prefers-reduced-motion (static resting pose).
 */
const LiveScreen = ({ dir = 1, src, alt, width = 252, glowColor, priority = false }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();

  // Scroll pose — plain-JS scroll engine
  const scrollProgress = useViewportProgress(ref);
  const p = useSpring(scrollProgress, { stiffness: 70, damping: 20 });
  const rotateY = useTransform(p, [0, 0.5, 1], [26 * dir, 8 * dir, -3 * dir]);
  const rotateX = useTransform(p, [0, 0.5, 1], [10, 1, -6]);
  const y = useTransform(p, [0, 0.5, 1], [64, 0, -48]);
  const scale = useTransform(p, [0, 0.5, 1], [0.91, 1, 0.965]);

  // Hover pose (additive, inner layer)
  const hx = useMotionValue(0);
  const hy = useMotionValue(0);
  const shx = useSpring(hx, { stiffness: 130, damping: 15 });
  const shy = useSpring(hy, { stiffness: 130, damping: 15 });
  const hoverRotY = useTransform(shx, [-0.5, 0.5], [-7, 7]);
  const hoverRotX = useTransform(shy, [-0.5, 0.5], [7, -7]);

  const handleMove = (e) => {
    if (reduce) return;
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    hx.set((e.clientX - r.left) / r.width - 0.5);
    hy.set((e.clientY - r.top) / r.height - 0.5);
  };
  const handleLeave = () => {
    hx.set(0);
    hy.set(0);
  };

  return (
    <div
      ref={ref}
      style={{ perspective: 1400 }}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
    >
      <motion.div
        style={
          reduce
            ? { transform: `rotateY(${8 * dir}deg)` }
            : { rotateY, rotateX, y, scale, transformStyle: 'preserve-3d' }
        }
      >
        <motion.div
          style={reduce ? {} : { rotateY: hoverRotY, rotateX: hoverRotX, transformStyle: 'preserve-3d' }}
        >
          <PhoneFrame
            src={src}
            alt={alt}
            width={width}
            glowColor={glowColor}
            priority={priority}
          />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default LiveScreen;
